import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import media from '../style/media';
import LogoImg from '../assets/logo_beaive.svg';
import TopBar from '../components/TopBar';
import SettingsBars from '../components/SettingsBars';
import { useCameras } from '../hooks';
import { Flex } from '../components/shared/Flex';

import { PageContainer, ContentWrapper, Header, Title, Subtitle, GlassCard, Grid } from '../style/layout';
import { User, Lock, Info, FileText, ShieldAlert, ChevronRight, HelpCircle } from 'lucide-react';

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  background: white;
  color: #0f172a;
  border: 1px solid #f1f5f9;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  text-align: left;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
    border-color: #e2e8f0;
    
    .icon-wrapper {
      background: #f1f5f9;
      transform: scale(1.1);
    }
  }

  &:active {
    transform: translateY(-4px);
  }

  .icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1.25rem;
    transition: all 0.3s ease;
    color: #3b82f6;

    svg {
      width: 22px;
      height: 22px;
    }
  }

  .content {
    flex: 1;
  }

  .label {
    display: block;
    font-size: 1.125rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }

  .description {
    font-size: 0.875rem;
    color: #64748b;
    font-weight: 400;
  }
`;

const AccessDeniedMessage = styled.div`
  text-align: center;
  padding: 3rem;
  background-color: rgba(254, 242, 242, 0.8);
  border: 1px solid rgba(254, 202, 202, 0.5);
  border-radius: 24px;
  color: #991b1b;

  h3 {
    color: #991b1b;
    margin-top: 0;
    font-size: 1.5rem;
    font-weight: 700;
  }

  p {
    margin: 0.75rem 0;
    font-size: 1rem;
    opacity: 0.9;
  }
`;

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const cameras = useCameras();

  useEffect(() => {
    const referrer = document.referrer;
    const isFromDashboard = referrer.includes('/dashboard') ||
      referrer.includes(window.location.origin + '/') ||
      referrer === '';

    if (!isFromDashboard) {
      setShowAccessDenied(true);
      const timer = setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [navigate]);

  const toggleSettings = useCallback(() => {
    setIsSettingsOpen(prev => !prev);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  const updateLicenseStatus = useCallback((valid: boolean) => {
    setIsLicenseValid(valid);
  }, []);

  const settingsItems = [
    {
      label: 'Profile',
      description: 'View your license and account details',
      icon: User,
      color: '#3b82f6',
      onClick: () => navigate('/settings/profile')
    },
    {
      label: 'Change Password',
      description: 'Update your account security',
      icon: Lock,
      color: '#ec4899',
      onClick: () => navigate('/settings/change-password')
    },
    {
      label: 'About Us',
      description: 'Learn more about Beaive technology',
      icon: HelpCircle,
      color: '#10b981',
      url: 'https://beaive.com/#about'
    },
    {
      label: 'Terms & Conditions',
      description: 'Review our service usage rules',
      icon: FileText,
      color: '#64748b',
      url: 'https://beaive.com/terms'
    },
    {
      label: 'Privacy Policy',
      description: 'How we protect your data',
      icon: ShieldAlert,
      color: '#f59e0b',
      url: 'https://beaive.com/privacy'
    },
    {
      label: 'Disclaimer',
      description: 'Medical and legal information',
      icon: Info,
      color: '#475569',
      url: 'https://beaive.com/disclaimer'
    }
  ];

  return (
    <PageContainer>
      <TopBar onSettingsClick={toggleSettings} isMeasuring={false} />
      <SettingsBars
        open={isSettingsOpen}
        onClose={handleCloseSettings}
        cameras={cameras}
        isLicenseValid={isLicenseValid}
      />
      <ContentWrapper>
        {showAccessDenied ? (
          <GlassCard>
            <AccessDeniedMessage>
              <h3>Access Denied</h3>
              <p>Please access Settings through the Dashboard.</p>
              <p>Redirecting you back to Dashboard...</p>
            </AccessDeniedMessage>
          </GlassCard>
        ) : (
          <GlassCard>
            <Header>
              <Title>Settings</Title>
              <Subtitle>Manage your account preferences and information</Subtitle>
            </Header>

            <Grid>
              {settingsItems.map((item, idx) => (
                <MenuButton
                  key={idx}
                  onClick={item.url ? () => window.open(item.url, '_blank', 'noopener') : item.onClick}
                >
                  <div className="icon-wrapper" style={{ color: item.color }}>
                    <item.icon />
                  </div>
                  <div className="content">
                    <span className="label">{item.label}</span>
                    <span className="description">{item.description}</span>
                  </div>
                  <ChevronRight size={18} color="#cbd5e1" />
                </MenuButton>
              ))}
            </Grid>
          </GlassCard>
        )}
      </ContentWrapper>
    </PageContainer>
  );
};

export default Settings;